import React, { useEffect } from 'react'
import { CheckboxItem } from '@sainsburys-tech/bolt'
import * as Styled from './QandAForm.styles'

const QandAFormView = ({ ...props }) => {
  const { formData, setInput, setCheckInput, validateInput, question, validateEmail, type, handleSubmit } = props
  const { questionError, nicknameError, emailError, acceptTsandCsError, yourAnswerError } = formData
  const additionalDetailsError = formData?.additionalDetails?.length >= 500
  const yourAnswerDetailsError = formData?.yourAnswer?.length >= 500
  const BRAND = process.env.BRAND || 'ARGOS'

  useEffect(() => {
    let id = ''
    if (acceptTsandCsError) id = 'acceptTsandCs-error-message'
    if (emailError) id = 'email-error-message'
    if (nicknameError) id = 'nickname-error-message'
    if (questionError) id = 'question-error-message'
    if (yourAnswerError) id = 'your-answer-error-message'
    const elem = document.getElementById(id)
    if (elem) elem.scrollIntoView()
  }, [questionError, nicknameError, emailError, acceptTsandCsError, yourAnswerError])

  return (
    <div>
      {type === 'question' && (
        <Styled.QandAFormat type={type}>
          <Styled.SubHeading>How to write your question</Styled.SubHeading>
          <p>We'd like to publish your question, so please</p>
          <ul>
            <li>keep your question about the product</li>
            <li>don't write about availability (the website updates when stock arrives)</li>
            <li>don't write about customer service (you can contact us if you need help)</li>
            <li>don't mention other shops or specific price you paid for the product</li>
            <li>don't include any info that could identify someone, like a full name</li>
          </ul>
        </Styled.QandAFormat>
      )}
      {type === 'answer' && (
        <Styled.QandAFormat type={type}>
          <Styled.AnswerHeading>
            <Styled.IconWrapper>
              <Styled.Icon>Q</Styled.Icon>
            </Styled.IconWrapper>
            <Styled.SubHeading>{question.attributes.title}</Styled.SubHeading>
          </Styled.AnswerHeading>
          <Styled.QuestionDetails>
            <p>Question from {question.attributes.profile.name}</p>
            <p>{question.attributes.profile.location && `Location: ${question.attributes.profile.location}`}</p>
            <p>{question.attributes.profile.age && `Age: ${question.attributes.profile.age}`}</p>
            <p>{question.attributes.profile.gender && `Gender: ${question.attributes.profile.gender}`}</p>
          </Styled.QuestionDetails>
        </Styled.QandAFormat>
      )}
      <form onSubmit={handleSubmit} method='post' data-test='questions-form'>
        {type === 'question' && (
          <Styled.InputField
            type='text'
            name='question'
            label='Enter your question'
            hint='Example: How many are in a package?'
            error={formData.questionError}
            value={formData.question}
            onChange={setInput('question')}
            onBlur={validateInput('question')}
          />
        )}
        <Styled.AdditionalDetailsWrapper>
          {yourAnswerError && <Styled.ErrorMessage>Please enter your answer</Styled.ErrorMessage>}
          {type === 'question' ? (
            <label htmlFor='details'>
              Additional details <span>Optional</span>
            </label>
          ) : (
            <label htmlFor='details'>Your Answer</label>
          )}
          {(additionalDetailsError || yourAnswerDetailsError) && (
            <Styled.ErrorMessage>You can only enter a maximum of 500 characters</Styled.ErrorMessage>
          )}
          <Styled.AdditionalDetails
            id='details'
            name={type === 'question' ? 'additionalDetails' : 'yourAnswer'}
            rows='7'
            maxLength='500'
            value={type === 'question' ? formData.additionalDetails : formData.yourAnswer}
            error={type === 'answer' && formData.yourAnswerError}
            onChange={setInput(type === 'question' ? 'additionalDetails' : 'yourAnswer')}
            onBlur={validateInput(type === 'question' ? 'additionalDetails' : 'yourAnswer')}
          />
          <Styled.WordCount error={type === 'question' ? additionalDetailsError : yourAnswerDetailsError}>
            {type === 'question' ? formData?.additionalDetails?.length : formData?.yourAnswer?.length}/500
          </Styled.WordCount>
        </Styled.AdditionalDetailsWrapper>
        <Styled.FormDivider />
        <Styled.SubHeading>About you</Styled.SubHeading>
        <Styled.InputField
          type='text'
          name='nickname'
          label='Nickname'
          hint={`This appears on the ${BRAND} site. To protect your privacy, always use your nickname, not your full name.`}
          error={formData.nicknameError}
          value={formData.nickname}
          onChange={setInput('nickname')}
          onBlur={validateInput('nickname')}
        />
        <Styled.InputField
          type='text'
          name='location'
          label='Location'
          optional='Optional'
          value={formData.location}
          onChange={setInput('location')}
        />
        {type === 'question' && (
          <Styled.SelectInput
            name='age'
            label='Age'
            optional='Optional'
            placeholder='Select'
            value={formData.age}
            onChange={setInput('age')}
            options={[
              { title: '16 - 24', value: '16 to 24' },
              { title: '25 - 34', value: '25 to 34' },
              { title: '35 - 55', value: '35 to 54' },
              { title: '55 - 74', value: '55 to 74' },
              { title: '75 and over', value: '75 and over' }
            ]}
          />
        )}
        {type === 'question' && (
          <Styled.SelectInput
            name='gender'
            label='Gender'
            optional='Optional'
            placeholder='Select'
            value={formData.gender}
            onChange={setInput('gender')}
            options={[
              { title: 'Male', value: 'Male' },
              { title: 'Female', value: 'Female' },
              { title: 'Prefer not to say', value: 'Rather not say' }
            ]}
          />
        )}
        {type === 'question' && (
          <CheckboxItem
            label='Please send an email when my question is posted'
            onChange={setCheckInput('emailQuestionPosted')}
            checked={formData.emailQuestionPosted}
            id='0'
          />
        )}
        <CheckboxItem
          label={
            type === 'queston'
              ? 'Please send an email when my question is answered'
              : 'Please send an email when my answer is posted'
          }
          onChange={setCheckInput(type === 'question' ? 'emailQuestionAnswered' : 'emailAnswerPosted')}
          checked={type === 'question' ? formData.emailQuestionAnswered : formData.emailAnswerPosted}
          id='1'
        />
        <Styled.InputField
          type='email'
          name='email'
          label='Email'
          hint='We will ONLY use your email to notify you in regards to your submission'
          error={formData.emailError}
          value={formData.email}
          onChange={setInput('email')}
          onBlur={validateEmail}
        />

        {formData.acceptTsandCsError && (
          <Styled.ErrorMessage id='acceptTsandCs-error-message'>{formData.acceptTsandCsError}</Styled.ErrorMessage>
        )}
        <CheckboxItem
          label={
            <>
              I accept the&nbsp;
              <Styled.TandCLabel href='https://documents.4rgos.it/v1/static/QA%20Terms%20of%20Use' target='_blank'>
                T's &amp; C's
              </Styled.TandCLabel>
              .&#13;&nbsp;You may receive emails regarding this submission, any email will include the ability to opt
              out of future communications{' '}
            </>
          }
          onChange={setCheckInput('acceptTsandCs')}
          checked={formData.acceptTsandCs}
          isError={formData.acceptTsandCsError}
          id='3'
        />
        <Styled.ButtonContainer>
          <Styled.ButtonWrapper kind='primary' type='submit' full>
            Submit
          </Styled.ButtonWrapper>
          <Styled.ButtonWrapper kind='secondary' type='button' full onClick={event => handleSubmit(event, 'preview')}>
            Preview
          </Styled.ButtonWrapper>
        </Styled.ButtonContainer>
      </form>
    </div>
  )
}

export default QandAFormView
